import { type TableProps, Tag } from 'antd';
import { type JSX } from 'react';

import { getTranslatedMessage } from '@client/utils/i18n.util';
import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import { tsToLocaleDateTime } from '@common/utils';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type { ELocales, ILedgerEntry } from '@common/types';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: ILedgerEntry[];
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<ILedgerEntry>['columns'] => {
  const { locale, sortedInfo, entities, filteredInfo } = props;

  const { cached, updateCached } = columnsCache<ILedgerEntry>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
  });

  if (cached) {
    return cached;
  }

  const t = getTranslatedMessage;

  const columns = [
    indexColumn,
    {
      title: t('form.credits.kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 120,
      align: 'center' as const,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'kind',
      ) as TColumnFilter<ILedgerEntry>),
      ...columnSorter(sortedInfo, 'kind', 'kind', 1),
      render: (kind: string): JSX.Element => <Tag>{kind}</Tag>,
    },
    {
      title: t('form.credits.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'amount', 'amount', 2),
      render: (amount: number): JSX.Element => <span>{amount}</span>,
    },
    {
      title: t('form.credits.operation'),
      dataIndex: 'operation',
      key: 'operation',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'operation',
      ) as TColumnFilter<ILedgerEntry>),
      render: (operation: string | null): JSX.Element => (
        <span>{operation ?? '—'}</span>
      ),
    },
    {
      title: t('form.credits.referenceId'),
      dataIndex: 'referenceId',
      key: 'referenceId',
      concealable: true,
      render: (referenceId: string | null): JSX.Element => (
        <span>{referenceId ?? '—'}</span>
      ),
    },
    {
      title: t('form.credits.userId'),
      dataIndex: 'userId',
      key: 'userId',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'userId',
      ) as TColumnFilter<ILedgerEntry>),
    },
    {
      title: t('form.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      align: 'center' as const,
      concealable: true,
      ...columnSorter(sortedInfo, 'createdAt', 'createdAt', 3),
      render: (createdAt: Date): JSX.Element => (
        <div>{tsToLocaleDateTime(createdAt)}</div>
      ),
    },
  ];

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
